import {
  DeesElement,
  property,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';

import { themeDefaultStyles } from '../../00theme.js';
import { SparkleField } from './dees-sparklebox.engine.js';
import { demoFunc } from './dees-sparklebox.demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-sparklebox': DeesSparklebox;
  }
}

const DEFAULT_COLORS_DARK = ['#fef08a', '#fde047', '#eab308'];
const DEFAULT_COLORS_BRIGHT = ['#555555', '#999999', '#cccccc'];

/**
 * dees-sparklebox
 *
 * A decorative, theme-aware sparkle/pixel field that animates in when it
 * scrolls into view. Self-contained: the animation runs on the main thread
 * (no web worker), respects `prefers-reduced-motion`, and fades into the
 * surrounding surface via a vignette. Place content over it through the default
 * slot.
 *
 * @slot - content rendered above the sparkle field.
 * @csspart -
 * @cssprop --dees-sparklebox-background - the color the field fades into
 *   (defaults to `var(--dees-color-bg-primary)`).
 */
@customElement('dees-sparklebox')
export class DeesSparklebox extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Layout'];

  /** Comma-separated sparkle colors. Empty = theme-aware defaults. */
  @property({ type: String })
  accessor colors: string = '';

  /** Pixel grid spacing in px (clamped 4–50). Smaller = denser. */
  @property({ type: Number })
  accessor gap: number = 4;

  /** Shimmer speed on a 0–100 scale. */
  @property({ type: Number })
  accessor speed: number = 35;

  /** Render the vignette that fades the field into the surrounding background. */
  @property({ type: Boolean })
  accessor vignette: boolean = true;

  public static styles: CSSResult[] = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        position: relative;
        width: 100%;
        height: 320px;
        overflow: hidden;
        --dees-sparklebox-background: var(--dees-color-bg-primary, #0a0a0a);
        background: var(--dees-sparklebox-background);
      }

      canvas {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: block;
        pointer-events: none;
      }

      .vignette {
        position: absolute;
        left: 0;
        right: 0;
        pointer-events: none;
      }
      .vignette.radial {
        top: 0;
        bottom: 0;
        background-image: radial-gradient(
          circle at center,
          transparent,
          var(--dees-sparklebox-background)
        );
      }
      .vignette.top {
        top: 0;
        height: 20%;
        background-image: linear-gradient(to top, transparent, var(--dees-sparklebox-background));
      }
      .vignette.bottom {
        top: 0;
        bottom: 0;
        background-image: linear-gradient(to bottom, transparent, var(--dees-sparklebox-background));
      }

      ::slotted(*) {
        position: relative;
        z-index: 1;
      }
    `,
  ];

  private canvasEl?: HTMLCanvasElement;
  private field?: SparkleField;
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  private reducedMotion = false;
  private isVisible = false;

  public render(): TemplateResult {
    return html`
      <canvas></canvas>
      ${this.vignette
        ? html`
            <div class="vignette radial"></div>
            <div class="vignette top"></div>
            <div class="vignette bottom"></div>
          `
        : ''}
      <slot></slot>
    `;
  }

  public async firstUpdated(): Promise<void> {
    const canvas = this.shadowRoot?.querySelector('canvas') ?? undefined;
    if (!canvas) {
      return;
    }
    this.canvasEl = canvas;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.field = new SparkleField(canvas);

    this.applyOptions();
    this.syncSize();

    this.resizeObserver = new ResizeObserver(() => this.syncSize());
    this.resizeObserver.observe(this);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.isVisible = entry.isIntersecting;
          this.field?.play(entry.isIntersecting ? 'appear' : 'disappear');
        }
      },
      { threshold: 0.1 },
    );
    this.intersectionObserver.observe(this);
  }

  public async disconnectedCallback(): Promise<void> {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
    this.field?.destroy();
    this.field = undefined;
    this.canvasEl = undefined;
    await super.disconnectedCallback?.();
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);
    if (
      this.field &&
      (changedProperties.has('colors') ||
        changedProperties.has('gap') ||
        changedProperties.has('speed'))
    ) {
      this.applyOptions();
      this.field.play(this.isVisible ? 'appear' : 'disappear');
    }
  }

  /** Re-resolve theme-aware default colors when the theme flips. */
  protected themeChanged(): void {
    if (!this.colors.trim() && this.field) {
      this.applyOptions();
      this.field.play(this.isVisible ? 'appear' : 'disappear');
    }
  }

  private resolveColors(): string[] {
    const custom = this.colors
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (custom.length) {
      return custom;
    }
    return this.goBright ? DEFAULT_COLORS_BRIGHT : DEFAULT_COLORS_DARK;
  }

  private applyOptions(): void {
    this.field?.configure({
      gap: this.gap,
      colors: this.resolveColors(),
      speed: this.reducedMotion ? 0 : this.speed,
    });
  }

  private syncSize(): void {
    if (!this.canvasEl || !this.field) {
      return;
    }
    const rect = this.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    this.field.resize(width, height);
    this.field.play(this.isVisible ? 'appear' : 'disappear');
  }
}
